import { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { apiClient } from "../../../../api/httpClient";
import { toast } from "react-toastify";
import { updateBondNumber, calculatePremium } from "../../../../globals/utils/commons";
import { extractErrorMessage } from "../../../../globals/utils/error-handler";
import { increment } from "../../../../store/slices/countSlice";
import {
  useBondRetrievalChat,
  useBondRetrievalChatUnread,
  useBondRetrievalEndorsementHistory,
  useVaObligeeResolver,
  resolveObligeeInformation,
  getObligeeIdFromResponse,
} from "./useBondRetrievalCore";
import { isoToDatetimeLocal } from "../bondRetrievalUtils";
import { mapIndemnityReceivedToYesNo } from "../bondHelpers";
import { bondRetrievalPageConfig } from "../bondRetrievalPageConfig";
import {
  mapRetrieveResponseToGlobalStateMerge,
  mergeGuardianshipGuardiansFromPatchResponse,
} from "../bondRetrievalApiMappers";
import { useBondRetrievalWorkflow } from "./useBondRetrievalWorkflow";
import useBondRetrievalUnderwritingActions from "./useBondRetrievalUnderwritingActions";
import {
  getBondRetrievalCorrectionBuilder,
  buildChangesTableHtml,
  shouldUseEndorsePath,
  getCorrectionContactInfo,
  getCorrectionRoleLabel,
} from "../bondRetrievalDataCorrectionBuilders";

const INITIAL_GLOBAL_STATE = {
  basicInformation: {},
  generalInformation: {},
  guardianDetails: {},
  attorneyInformation: {},
  obligeeInformation: {},
  requestorInformation: {},
  documentUpload: {},
  billingInformation: {},
  bondNumber: {},
};

async function postAmountEndorsementChanges({
  changes,
  bond_id,
  contactName,
  contactEmail,
  file,
}) {
  const amountChange = changes.find((c) => c.field === "Bond Amount");
  if (!amountChange) return;

  const oldNum = Number(amountChange.oldValue) || 0;
  const newNum = Number(amountChange.newValue) || 0;
  if (oldNum === newNum) return;

  const endorsementType = newNum > oldNum ? "Increase" : "Decrease";
  const formData = new FormData();
  formData.append("endorsement_type", endorsementType);
  formData.append("updated_bond_amount", String(newNum));
  formData.append("contact_name", contactName);
  formData.append("contact_email", contactEmail);
  if (file) {
    formData.append("endorsement_file", file);
  }

  await apiClient(
    `/endorse-bond/`,
    "POST",
    formData,
    { bond_id },
    { "Content-Type": "multipart/form-data" }
  );
}

async function postEndorsementChanges({ changes, bond_id, contactName, contactEmail, principalFieldName }) {
  const toTitleCase = (str) =>
    (str || "")
      .trim()
      .split(/\s+/)
      .filter(Boolean)
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
      .join(" ");

  for (const change of changes) {
    if (change.field === "County") {
      const formData = new FormData();
      formData.append("endorsement_type", "Name_Change_County");
      formData.append("updated_name", toTitleCase(change.newValue));
      formData.append("contact_name", contactName);
      formData.append("contact_email", contactEmail);
      await apiClient(`/endorse-bond/`, "POST", formData, { bond_id }, { "Content-Type": "multipart/form-data" });
    } else if (change.field === principalFieldName) {
      const formData = new FormData();
      formData.append("endorsement_type", "Name_Change_Principal");
      formData.append("updated_name", (change.newValue ?? "").toString().trim());
      const principalId = change.principalId ?? change.trusteeId;
      if (principalId != null) {
        formData.append("principal_id", String(principalId));
      }
      if (change.trusteeId != null) {
        formData.append("trustee_id", String(change.trusteeId));
      }
      formData.append("contact_name", contactName);
      formData.append("contact_email", contactEmail);
      await apiClient(`/endorse-bond/`, "POST", formData, { bond_id }, { "Content-Type": "multipart/form-data" });
    }
  }
}

/**
 * Shared bond retrieval hook: global state, fetch-on-mount, finish/sign,
 * underwriting overrides, credit report, and data-correction state.
 *
 * @param {import('../bondRetrievalPageConfig').BondRetrievalKind} kind
 * @param {object} options
 * @param {(globalState: object, status: string, billingMethod: string, isIndemnityReceived: string) => object} options.buildPayload
 * @param {(amount: number, context: { globalState: object, data: object|null }) => number} [options.calculateCorrectionPremium]
 * @param {() => object} [options.extraReturn]
 */
export function useBondRetrieveBase(kind, { buildPayload, calculateCorrectionPremium, extraReturn } = {}) {
  const pageCfg = bondRetrievalPageConfig[kind];
  const correctionBuilder = getBondRetrievalCorrectionBuilder(kind);
  const { bond_id } = useParams();

  const [isLoading, setIsLoading] = useState(false);
  const [isEsignatureLoading, setIsEsignatureLoading] = useState(false);
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const [billingMethod, setBillingMethod] = useState("");
  const [isIndemnityReceived, setIsIndemnityReceived] = useState("");
  const [isApproveLoading, setIsApproveLoading] = useState(false);
  const [isRejectLoading, setIsRejectLoading] = useState(false);
  const { openChat, setOpenChat, chatUnreadTotal, setChatUnreadTotal } = useBondRetrievalChat();
  const {
    isHistoryOpen,
    setIsHistoryOpen,
    endorsementLogs,
    isHistoryLoading,
    historyTab,
    setHistoryTab,
    fetchEndorsementLogs,
    toggleHistoryDrawer,
  } = useBondRetrievalEndorsementHistory(bond_id);
  const [data, setData] = useState(null);
  const [bondUpdates, setBondUpdates] = useState([]);
  const { role } = useSelector((state) => state.auth);
  const disabled = role !== "super_user" && role !== "internal_user";
  const dispatch = useDispatch();
  const username = useSelector((state) => state.auth?.user?.username) || "";
  const userdetails = useSelector((state) => state.auth.user);

  const [isCreditDialogOpen, setIsCreditDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectFcraFile, setRejectFcraFile] = useState(null);
  const [rejectFcraUploading, setRejectFcraUploading] = useState(false);
  const [creditReportFile, setCreditReportFile] = useState(null);
  const [isCreditUploading, setIsCreditUploading] = useState(false);
  const [editableCreatedAt, setEditableCreatedAt] = useState("");
  const [globalState, setGlobalState] = useState(INITIAL_GLOBAL_STATE);

  const vaObligee = useVaObligeeResolver(bond_id);
  const resolveObligeeInformationFromResponse = useCallback(
    (responseData) =>
      resolveObligeeInformation(
        responseData,
        pageCfg.nestedKey,
        pageCfg.obligeeResolverLogLabel || `${kind}BondRetrieve`
      ),
    [pageCfg.nestedKey, pageCfg.obligeeResolverLogLabel, kind]
  );

  useBondRetrievalChatUnread(bond_id, role, setChatUnreadTotal);

  const generatePayloadForStatus = useCallback(
    (status) => buildPayload(globalState, status, billingMethod, isIndemnityReceived),
    [buildPayload, globalState, billingMethod, isIndemnityReceived]
  );

  const {
    anchorEl,
    setAnchorEl,
    handleApprove,
    handleReject,
    handleUpdateUnderwriting,
    isUnderwritingLoading,
    setIsUnderwritingLoading,
    handleButtonClick,
    handleMenuClose,
    handleOptionSelect,
  } = useBondRetrievalWorkflow({
    kind,
    bond_id,
    data,
    globalState,
    billingMethod,
    dispatch,
    generatePayloadForStatus,
    setRejectDialogOpen,
    setRejectFcraFile,
    setIsApproveLoading,
    setIsRejectLoading,
    setGlobalState,
  });

  const getMapperOptions = useCallback(() => {
    if (pageCfg.obligeeResolverStrategy === "va") {
      return { resolveVaObligeeInformation: vaObligee.resolveVaObligeeInformation };
    }
    return { resolveObligeeInformationFromResponse };
  }, [pageCfg.obligeeResolverStrategy, vaObligee.resolveVaObligeeInformation, resolveObligeeInformationFromResponse]);

  useEffect(() => {
    const fetchBondData = async () => {
      setIsLoading(true);
      try {
        const response = await apiClient(pageCfg.retrievePath(bond_id));
        setData(response);
        setEditableCreatedAt(isoToDatetimeLocal(response?.created_at) || "");
        setBillingMethod(
          response[pageCfg.nestedKey]?.billing_method ??
            response?.billing_method ??
            ""
        );
        setIsIndemnityReceived(
          mapIndemnityReceivedToYesNo(response[pageCfg.nestedKey]?.is_indemnity_received)
        );

        if (pageCfg.fetchBondUpdatesOnMount) {
          try {
            const updates = await apiClient(`/bond-updates/${bond_id}/`, "GET");
            setBondUpdates(Array.isArray(updates) ? updates : []);
          } catch (e) {
            console.error("Failed to fetch bond updates:", e);
            setBondUpdates([]);
          }
        }

        if (!response) {
          throw new Error("No data found in the response");
        }

        setGlobalState((prevState) =>
          mapRetrieveResponseToGlobalStateMerge(kind, prevState, response, getMapperOptions())
        );
      } catch (error) {
        console.error("Error fetching bond data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchBondData();
  }, [bond_id, pageCfg, kind, getMapperOptions]);

  const handleFinish = async () => {
    setIsSubmitLoading(true);
    const payload = buildPayload(globalState, data?.status, billingMethod, isIndemnityReceived);
    if (editableCreatedAt) {
      payload.created_at = new Date(editableCreatedAt).toISOString();
    }

    const { bondNumber, ...payloadWithoutBondNumber } = payload;

    const currentBondNumber =
      data?.bond_number?.bondno_id != null ? Number(data.bond_number.bondno_id) : null;
    const newBondNumber =
      globalState.bondNumber?.bond_number?.bondno_id != null
        ? Number(globalState.bondNumber.bond_number.bondno_id)
        : null;

    if (newBondNumber != null && currentBondNumber !== newBondNumber) {
      try {
        await updateBondNumber(
          newBondNumber,
          Number(bond_id),
          globalState.attorneyInformation?.lawFirm
        );
      } catch (error) {
        console.error("API call error:", error);
        if (error.response?.data?.error) {
          console.error("Error response data:", error.response.data);
          toast.error(`Bond number error: ${error.response.data.error}`);
        } else {
          toast.error("Failed to update bond number.");
        }
      }
    }

    try {
      const patchResponse = await apiClient(
        pageCfg.updatePath(bond_id),
        "PATCH",
        payloadWithoutBondNumber
      );
      if (kind === "guardianship" && patchResponse?.guardians) {
        setGlobalState((prev) =>
          mergeGuardianshipGuardiansFromPatchResponse(prev, patchResponse)
        );
      }
      toast.success("Bond updated successfully!");
      dispatch(increment());
    } catch (error) {
      console.error("Error updating the bond request:", error);
      const message = extractErrorMessage(error);
      toast.error(`Failed to update bond. ${message}`);
    } finally {
      setIsSubmitLoading(false);
    }
  };

  const handleSignIndemnity = async () => {
    setIsEsignatureLoading(true);
    try {
      await apiClient(`/bond-signature/${bond_id}/`, "POST");
      toast.success("E-signature request sent successfully");
    } catch (error) {
      toast.error("Failed to send e-signature request.");
      console.error(error);
    } finally {
      setIsEsignatureLoading(false);
    }
  };

  const updateGlobalState = (stepKey, stepData) => {
    setGlobalState((prevState) => ({
      ...prevState,
      [stepKey]: stepData,
    }));
  };

  const underwritingActions = useBondRetrievalUnderwritingActions({
    bond_id,
    userdetails,
    setIsUnderwritingLoading,
  });

  const [isDataCorrectionOpen, setIsDataCorrectionOpen] = useState(false);
  const [dataCorrectionTab, setDataCorrectionTab] = useState(0);
  const [bondDataCorrectionsList, setBondDataCorrectionsList] = useState([]);
  const [bondDataCorrectionsLoading, setBondDataCorrectionsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [counties, setCounties] = useState([]);
  const [loadingCounties, setLoadingCounties] = useState(false);
  const [dataCorrectionForm, setDataCorrectionForm] = useState(() => correctionBuilder.getFormDefaults(null));

  const runCreditReport = async (bondId, file) => {
    const formData = new FormData();
    formData.append("credit_report_document", file);

    return await apiClient(
      `/run-credit-report/?bond_id=${bondId}`,
      "POST",
      formData,
      {},
      { "Content-Type": "multipart/form-data" }
    );
  };

  const fetchCounties = async () => {
    setLoadingCounties(true);
    try {
      const response = await apiClient(`/counties-by-state/6/`, "GET");
      const formattedCounties = response.map((county) => county.bond_county);
      setCounties(formattedCounties);
    } catch (error) {
      console.error("Error fetching counties:", error);
    } finally {
      setLoadingCounties(false);
    }
  };

  useEffect(() => {
    if (!isDataCorrectionOpen || dataCorrectionTab !== 1 || !bond_id) return;
    setBondDataCorrectionsLoading(true);
    apiClient(`/bond-data-corrections/bond/${bond_id}/`, "GET", null, { limit: 100, offset: 0 })
      .then((res) => setBondDataCorrectionsList(Array.isArray(res) ? res : []))
      .catch((e) => {
        if (e?.status !== 404) console.error(e);
        setBondDataCorrectionsList([]);
      })
      .finally(() => setBondDataCorrectionsLoading(false));
  }, [isDataCorrectionOpen, dataCorrectionTab, bond_id]);

  const handleOpenDataCorrection = () => {
    fetchCounties();
    setDataCorrectionForm(correctionBuilder.getFormDefaults(data));
    setIsDataCorrectionOpen(true);
  };

  const resolveCorrectionPremium =
    calculateCorrectionPremium ||
    ((amount) => calculatePremium(amount));

  const handleBondAmountChange = (e) => {
    const newBondAmount = e.target.value;
    const formattedAmount = Math.round(Number.parseFloat(newBondAmount));
    const newPremium = resolveCorrectionPremium(formattedAmount, { globalState, data });
    setDataCorrectionForm((prev) => ({
      ...prev,
      bondAmount: formattedAmount,
      premium: newPremium,
    }));
  };

  const postBondUpdates = async (changes) => {
    const role_name = correctionBuilder.getBondUpdateRoleLabel(role);
    const payload = changes.map((change) => ({
      requester_id: userdetails?.id,
      requester_name: `${userdetails?.first_name} ${userdetails?.last_name} (${role_name})`,
      bond_id,
      bond_type: pageCfg.correctionBondType,
      field_name: change.field,
      old_value: change.oldValue,
      new_value: change.newValue,
    }));

    try {
      await apiClient(`/bond-updates/`, "POST", payload, {}, { "Content-Type": "application/json" });
    } catch (error) {
      console.error("Error posting bond updates:", error);
    }
  };

  const refreshBondDataAfterCorrection = async () => {
    const response = await apiClient(pageCfg.retrievePath(bond_id));
    setData(response);
    setEditableCreatedAt(isoToDatetimeLocal(response?.created_at) || "");
    setBillingMethod(
      response[pageCfg.nestedKey]?.billing_method ??
        response?.billing_method ??
        ""
    );
    setIsIndemnityReceived(
      mapIndemnityReceivedToYesNo(response[pageCfg.nestedKey]?.is_indemnity_received)
    );
    return response;
  };

  const logBondUpdates = async (changes) => {
    try {
      if (changes.length > 0) {
        await postBondUpdates(changes);
      }
    } catch (e) {
      console.error("Bond updates logging error:", e);
    }
  };

  const createCorrectionRecordAndNotify = async ({
    response,
    form,
    contactName,
    contactEmail,
    changes,
    changesTableHtml,
  }) => {
    const toSnakeCase = (s) => (s || "").toString().toLowerCase().replace(/\s+/g, "_");
    const roleLabel = getCorrectionRoleLabel(role);

    try {
      await apiClient(
        `/bond-data-corrections/`,
        "POST",
        {
          bond_id: Number(bond_id),
          bond_type: pageCfg.correctionBondType,
          bond_number: (
            response?.bond_number?.bond_number ||
            response?.bond_number?.bondno_id ||
            ""
          ).toString(),
          bond_summary:
            changes.length > 0
              ? "Corrected: " + changes.map((c) => c.field).join(", ")
              : "Data correction",
          reason: form.reason || "",
          changes: changes.map((c) => ({
            field: toSnakeCase(c.field),
            from: String(c.oldValue ?? ""),
            to: String(c.newValue ?? ""),
          })),
          requester_info: { name: contactName, email: contactEmail, role: roleLabel },
        },
        {},
        { "Content-Type": "application/json" }
      );
    } catch (e) {
      console.error("Bond data correction record error:", e);
    }

    try {
      await apiClient(
        `/send-portal-email/`,
        "POST",
        {
          subject: pageCfg.correctionEmailSubject,
          content_html: `<p>Please review the following item.</p>
<p><strong>Requester:</strong> ${userdetails?.first_name} ${userdetails?.last_name}</p>
<p><strong>Bond #:</strong> ${response?.bond_number?.bond_number || response?.bond_number?.bondno_id || ""}</p>
${correctionBuilder.buildEmailSummaryHtml(response)}
<p><strong>Reason for Change:</strong> ${form.reason}</p>
${changesTableHtml}`,
          navigate_path: pageCfg.correctionNavigatePath(bond_id),
          button_text: "Review in Portal",
        },
        {},
        { "Content-Type": "application/json" }
      );
    } catch (e) {
      console.error("Email notification error:", e);
    }
  };

  const handleSubmitDataCorrection = async () => {
    try {
      if (!dataCorrectionForm.reason) {
        toast.error("Please provide a reason for the change");
        return;
      }

      setIsSubmitting(true);

      const payload = correctionBuilder.buildPayload(dataCorrectionForm, data);
      const changes = correctionBuilder.buildChanges(dataCorrectionForm, data);
      const bondAmountChanged = changes.some((c) => c.field === "Bond Amount");
      const useEndorsePath = shouldUseEndorsePath(
        changes,
        data?.created_at || null,
        pageCfg.correctionPrincipalField
      );
      const { contactName, contactEmail } = getCorrectionContactInfo(userdetails);

      if (bondAmountChanged) {
        if (!dataCorrectionForm.amountEndorsementFile) {
          toast.error(
            "Upload the court order document required for bond amount Increase/Decrease."
          );
          setIsSubmitting(false);
          return;
        }
        await postAmountEndorsementChanges({
          changes,
          bond_id,
          contactName,
          contactEmail,
          file: dataCorrectionForm.amountEndorsementFile,
        });
      }

      if (useEndorsePath) {
        await postEndorsementChanges({
          changes,
          bond_id,
          contactName,
          contactEmail,
          principalFieldName: pageCfg.correctionPrincipalField,
        });
      } else {
        await apiClient(pageCfg.updatePath(bond_id), "PATCH", payload);
      }

      const response = await refreshBondDataAfterCorrection();

      try {
        const updates = await apiClient(`/bond-updates/${bond_id}/`, "GET");
        setBondUpdates(Array.isArray(updates) ? updates : []);
      } catch (e) {
        console.error("Failed to fetch bond updates:", e);
        setBondUpdates([]);
      }

      const obligeeIdFromApi = getObligeeIdFromResponse(response, pageCfg.nestedKey);
      setGlobalState((prevState) =>
        correctionBuilder.mergeGlobalState(prevState, response, obligeeIdFromApi)
      );

      await logBondUpdates(changes);

      const changesTableHtml = buildChangesTableHtml(changes);

      if (!useEndorsePath) {
        await createCorrectionRecordAndNotify({
          response,
          form: dataCorrectionForm,
          contactName,
          contactEmail,
          changes,
          changesTableHtml,
        });
      }

      setIsDataCorrectionOpen(false);
      toast.success("Data updated successfully");
    } catch (error) {
      console.error("Error updating data:", error);
      toast.error("Failed to update data");
    } finally {
      setIsSubmitting(false);
    }
  };

  const baseReturn = {
    bond_id,
    isLoading,
    setIsLoading,
    isEsignatureLoading,
    isSubmitLoading,
    billingMethod,
    setBillingMethod,
    isIndemnityReceived,
    setIsIndemnityReceived,
    isApproveLoading,
    isRejectLoading,
    openChat,
    setOpenChat,
    chatUnreadTotal,
    isHistoryOpen,
    setIsHistoryOpen,
    endorsementLogs,
    isHistoryLoading,
    historyTab,
    setHistoryTab,
    data,
    setData,
    bondUpdates,
    setBondUpdates,
    role,
    disabled,
    dispatch,
    username,
    isCreditDialogOpen,
    setIsCreditDialogOpen,
    rejectDialogOpen,
    setRejectDialogOpen,
    rejectFcraFile,
    setRejectFcraFile,
    rejectFcraUploading,
    setRejectFcraUploading,
    creditReportFile,
    setCreditReportFile,
    isCreditUploading,
    setIsCreditUploading,
    editableCreatedAt,
    setEditableCreatedAt,
    anchorEl,
    setAnchorEl,
    globalState,
    setGlobalState,
    userdetails,
    updateGlobalState,
    handleFinish,
    handleSignIndemnity,
    handleApprove,
    handleReject,
    isUnderwritingLoading,
    setIsUnderwritingLoading,
    handleUpdateUnderwriting,
    handleButtonClick,
    handleMenuClose,
    handleOptionSelect,
    underwritingAnchorEl: underwritingActions.underwritingAnchorEl,
    setUnderwritingAnchorEl: underwritingActions.setUnderwritingAnchorEl,
    overrideReasonDialogOpen: underwritingActions.overrideReasonDialogOpen,
    setOverrideReasonDialogOpen: underwritingActions.setOverrideReasonDialogOpen,
    overrideReason: underwritingActions.overrideReason,
    setOverrideReason: underwritingActions.setOverrideReason,
    manualUnderwritingDialogOpen: underwritingActions.manualUnderwritingDialogOpen,
    setManualUnderwritingDialogOpen: underwritingActions.setManualUnderwritingDialogOpen,
    manualUnderwritingReason: underwritingActions.manualUnderwritingReason,
    setManualUnderwritingReason: underwritingActions.setManualUnderwritingReason,
    handleUnderwritingMenuClick: underwritingActions.handleUnderwritingMenuClick,
    handleUnderwritingMenuClose: underwritingActions.handleUnderwritingMenuClose,
    handleOverrideUnderwritingClick: underwritingActions.handleOverrideUnderwritingClick,
    handleManualUnderwritingClick: underwritingActions.handleManualUnderwritingClick,
    handleSubmitManualUnderwriting: underwritingActions.handleSubmitManualUnderwriting,
    handleSubmitOverrideReason: underwritingActions.handleSubmitOverrideReason,
    runCreditReport,
    isDataCorrectionOpen,
    setIsDataCorrectionOpen,
    dataCorrectionTab,
    setDataCorrectionTab,
    bondDataCorrectionsList,
    bondDataCorrectionsLoading,
    isSubmitting,
    setIsSubmitting,
    counties,
    loadingCounties,
    dataCorrectionForm,
    setDataCorrectionForm,
    fetchCounties,
    handleOpenDataCorrection,
    handleBondAmountChange,
    handleSubmitDataCorrection,
    postBondUpdates,
    getObligeeIdFromResponse,
    fetchEndorsementLogs,
    toggleHistoryDrawer,
    isoToDatetimeLocal,
  };

  if (pageCfg.obligeeResolverStrategy === "va") {
    baseReturn.obligeeAppliedForBondIdRef = vaObligee.obligeeAppliedForBondIdRef;
  }

  return {
    ...baseReturn,
    ...(extraReturn ? extraReturn() : {}),
  };
}

export { postAmountEndorsementChanges };
export default useBondRetrieveBase;
